import React from 'react';
import { ShieldCheckIcon, DocumentChartBarIcon, DevicePhoneMobileIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const FeaturesSection = () => {
  return (
    <div className="bg-kb-surface-raised py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl lg:text-center mb-16">
          <h2 className="text-base font-semibold leading-7 text-kb-surface-strong tracking-wide uppercase">Unmatched Capabilities</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-kb-surface-base sm:text-[2rem]">
            Everything you need to scale your business
          </p>
          <p className="mt-4 text-lg leading-8 text-kb-text-secondary">
            Replace your traditional Udhar bahi khata with a high-performance digital ledger system built for modern enterprises.
          </p>
        </div>
        
        {/* Bento Box Grid */}
        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            
            {/* Big Bento Box */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-gray-50 border border-gray-100 shadow-sm"
            >
              <div className="p-10 lg:p-12">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-kb-surface-strong shadow-lg mb-6">
                  <DocumentChartBarIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold tracking-tight text-kb-surface-base">Automated PDF Reports</h3>
                <p className="mt-4 text-base leading-7 text-kb-text-secondary">
                  Generate beautiful, enterprise-grade PDF statements instantly. Share professional account statements with your customers in one click, reducing disputes and accelerating payments.
                </p>
              </div>
              <div className="h-48 w-full bg-gradient-to-t from-gray-100 to-transparent mt-8 relative">
                 {/* Decorative element mimicking a report */}
                 <div className="absolute bottom-[-20px] right-10 w-64 h-40 bg-white rounded-t-xl shadow-2xl border border-gray-200 p-4 transform rotate-3">
                    <div className="h-4 w-1/2 bg-gray-200 rounded mb-4"></div>
                    <div className="h-2 w-full bg-gray-100 rounded mb-2"></div>
                    <div className="h-2 w-3/4 bg-gray-100 rounded mb-2"></div>
                    <div className="h-2 w-5/6 bg-gray-100 rounded mb-4"></div>
                    <div className="h-8 w-full bg-red-50 rounded"></div>
                 </div>
              </div>
            </motion.div>

            {/* Small Bento Box 1 */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="relative overflow-hidden rounded-3xl bg-[#0e1725] border border-gray-800 shadow-xl"
            >
              <div className="p-10">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 shadow-lg mb-6">
                  <ShieldCheckIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white">100% Safe & Secure</h3>
                <p className="mt-4 text-sm leading-6 text-gray-400">
                  Bank-level security and automated cloud backups. Access your data anytime, anywhere without fear of loss.
                </p>
              </div>
            </motion.div>

            {/* Small Bento Box 2 */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#dc343b] to-[#a32227] border border-red-800 shadow-xl"
            >
              <div className="p-10">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-black/10 shadow-lg mb-6">
                  <BellAlertIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-white">Real-time SMS Alerts</h3>
                <p className="mt-4 text-sm leading-6 text-red-100">
                  Keep your customers updated with free SMS on every transaction. Build trust with instant notifications.
                </p>
              </div>
            </motion.div>

            {/* Wide Bento Box */}
            <motion.div 
              whileHover={{ y: -5 }}
              className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-white border border-gray-100 shadow-sm flex flex-col sm:flex-row items-center"
            >
              <div className="p-10 lg:w-1/2">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#375a8d] shadow-lg mb-6">
                  <DevicePhoneMobileIcon className="h-6 w-6 text-white" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold tracking-tight text-kb-surface-base">Mobile Optimized Experience</h3>
                <p className="mt-4 text-sm leading-6 text-kb-text-secondary">
                  Access your ledger on any device. Our responsive PWA architecture ensures a native-like feel whether you are on mobile, tablet, or desktop.
                </p>
              </div>
              <div className="lg:w-1/2 h-full min-h-[200px] bg-gray-50 flex items-center justify-center p-8">
                 {/* Mock mobile phone frame */}
                 <div className="w-32 h-48 bg-white rounded-2xl shadow-lg border-[4px] border-gray-800 relative">
                    <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-gray-800 rounded-full"></div>
                    <div className="mt-6 mx-2 h-4 bg-gray-100 rounded mb-2"></div>
                    <div className="mx-2 h-10 bg-[#dc343b] opacity-20 rounded mb-2"></div>
                    <div className="mx-2 h-10 bg-green-500 opacity-20 rounded"></div>
                 </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default FeaturesSection;
